import ToggleLink from './components/toggleLink/toggleLink';
import Megadrop from './components/megadrop/megadrop';
import MobileMenu from './components/mobileMenu/mobileMenu';
import TabUsability from './components/tabUsability/tabUsability';
import EpiserverForm from './components/episerverForm/episerverForm';
import InnovationRegisterForm from './components/innovationRegisterForm/innovationRegisterForm';
import InnovationSuccessMessage from './components/innovationSuccessMessage/innovationSuccessMessage';
import { wrap } from './components/utilities/utilities';
import { setCookie, getCookie } from './components/cookie/cookie';
import Slider from './components/slider/slider';

/**
 * App
 * This starting point for this application.
 * @version 3.0.0
 * @exports App
 */
export default class App {
	constructor() {
		// Init menu
		new Megadrop();

		// Init mobile menu
		new MobileMenu();

		// Init toggle link
		new ToggleLink();

		// Init tabUsability
		new TabUsability();

		// Init innovation register form
		new InnovationRegisterForm();

		// Init innovation success message
		new InnovationSuccessMessage();

		// Init slider for mobile version
		new Slider();

		// Init table wrap for mobile scrolling of tables in rte.
		this._initRteTableWrap();

		// Init ajaxForm
		this._initAjaxForm();

		// Init episerverForms
		this._initEpiserverForm();

		// Demo plugins (for demo purpose only, remove this when you build a new web).
		this._loadPluginsDemonstration();

		this._initCookies();

		this._initDropdowns();

		this._initHideSearchField();

		//this._initStopAutoPlayOnVideo();

		this._initClearInputFieldButton();

		this._initModal();
	}

	_initModal() {
		let els = document.querySelectorAll('[data-type="modal"]');

		if (els.length > 0) {
			import(
				/* webpackChunkName: "mobile-menu" */ './components/modal/modal'
			).then(Modal => {
				for (let i = 0; i < els.length; i++) {
					let el = els[i];

					if (el.getAttribute('data-modal-initiated') === null) {
						new Modal.default(el);
						el.setAttribute('data-modal-initiated', true);
					}
				}
			});
		}
	}

	/**
	 * Function that initiates the ajax-form functionality.
	 * @private
	 */
	_initAjaxForm() {
		let els = document.querySelectorAll('[data-type="ajaxForm"]');

		if (els.length > 0) {
			import(
				/* webpackChunkName: "ajaxForm" */ './components/ajaxForm/ajaxForm'
			).then(AjaxForm => {
				for (let i = 0; i < els.length; i++) {
					let el = els[i];

					if (el.getAttribute('data-ajaxform-initiated') === null) {
						new AjaxForm.default(el);
						el.setAttribute('data-ajaxform-initiated', true);
					}
				}
			});
		}
	}

	/**
	 * Function that initiates the episerverform functionality.
	 * @private
	 */
	_initEpiserverForm() {
		let els = document.querySelectorAll('[data-type="episerverForm"]');

		if (els.length > 0) {
			for (let i = 0; i < els.length; i++) {
				let el = els[i];

				if (el.getAttribute('data-episerverform-initiated') === null) {
					new EpiserverForm(el);
					el.setAttribute('data-episerverform-initiated', true);
				}
			}
		}
	}

	/**
	 * Function that wraps all tables in rte with a div to enable the scroll-feature in mobile.
	 * @private
	 */
	_initRteTableWrap() {
		let els = document.querySelectorAll('.rte table');

		els.forEach(el => {
			let wrapper = document.createElement('div');
			wrapper.classList.add('table-scrollable');
			wrap(el, wrapper);
		});
	}

	/**
	 * Loads all plugins in the framework to demonstrate them (this should be removed on "real" webprojects).
	 * @todo Remove this function from "real" projects.
	 * @private
	 */
	_loadPluginsDemonstration() {
		if (document.querySelectorAll('[data-type="share"]').length > 0) {
			import(/* webpackChunkName: "share" */ './components/share/share').then(
				Share => {
					new Share.default();
				}
			);
		}

		if (document.querySelectorAll('[data-type="loadMore"]').length > 0) {
			import(
				/* webpackChunkName: "loadMore" */ './components/loadMore/loadMore'
			).then(LoadMore => {
				new LoadMore.default();
			});
		}

		if (document.querySelectorAll('[data-type="autosubmit"]').length > 0) {
			import(
				/* webpackChunkName: "autoSubmit" */ './components/autoSubmit/autoSubmit'
			).then(AutoSubmit => {
				new AutoSubmit.default();
			});
		}

		if (document.getElementById('autoembed-demo')) {
			import(
				/* webpackChunkName: "autoEmbed" */ './components/autoEmbed/autoEmbed'
			).then(AutoEmbed => {
				const testNode = document.getElementById('autoembed-demo');
				const input = testNode.innerHTML;
				const embed = new AutoEmbed.default();
				const htmlWithEmbeds = embed.parse(input);
				testNode.innerHTML = htmlWithEmbeds;
				embed.renderJsEmbeds();
			});
		}
	}

	/**
	 * Initates cookies and cookiebar
	 * @private
	 */
	_initCookies() {
		const cookiebar = document.querySelector('.cookiebar');
		const overlay = document.querySelector('.overlay');

		if (cookiebar) {
			if (!getCookie('acceptCookieCambio')) {
				cookiebar.classList.remove('cookiebar--hidden');

				let acceptCookie = cookiebar.querySelectorAll('.cookiebar__accept');

				acceptCookie.forEach(accept => {
					accept.addEventListener('click', e => {
						e.preventDefault();
						setCookie('acceptCookieCambio', true, 10950, '/');
						cookiebar.classList.add('cookiebar--hidden');
						overlay.classList.remove('overlay');
					});
				});
			} else {
				overlay.classList.remove('overlay');
			}
		}
	}

	/**
	 * This function applies the toggle functionallity on the language menus.
	 * @private
	 */
	_initDropdowns() {
		const initToggle = (toggleElement, contentDiv) => {
			let toggle = document.getElementById(toggleElement);
			if (toggle) {
				let usabillityElement = toggle.querySelector(
					'.usability--visuallyhidden'
				);
				let shevron = toggle.querySelector('.arrow');
				let content = document.getElementById(contentDiv);

				toggle.addEventListener('click', () => {
					if (toggle.getAttribute('aria-expanded') === 'true') {
						content.style.display = 'none';
						toggle.setAttribute('aria-expanded', 'false');
						usabillityElement.innerHTML = 'Expandera språkmeny';
						if (shevron) {
							shevron.classList.remove('icon-chevron-up');
							shevron.classList.add('icon-chevron-down');
						}
					} else {
						content.style.display = 'block';
						toggle.setAttribute('aria-expanded', 'true');
						usabillityElement.innerHTML = 'Dölj språkmeny';
						if (shevron) {
							shevron.classList.add('icon-chevron-up');
							shevron.classList.remove('icon-chevron-down');
						}
					}
				});
			}
		};

		initToggle('language-toggle-in-the-header', 'language-list-in-the-header');
		initToggle('language-toggle-in-the-tablet', 'language-list-in-the-tablet');
		initToggle('language-toggle-in-the-footer', 'language-list-in-the-footer');
		initToggle('language-toggle-in-mobile', 'language-list-in-mobile');
	}

	_initHideSearchField() {
		let body = document.getElementsByTagName('body')[0];
		let searchPane = document.getElementById('search-pane');
		let searchToggle = document.getElementById('search-button');

		searchToggle.addEventListener('click', () => {
			let inputField = searchPane.getElementsByTagName('input')[0];
			inputField.focus();
		});

		let searchBtnClear = document.getElementsByClassName(
			'search-field__button-close'
		)[0];

		searchBtnClear.addEventListener('click', () => {
			closeSearchField();
		});

		searchBtnClear.addEventListener('keydown', e => {
			let usingScreenReader = e.keyCode === 13 || e.keyCode === 32;

			if (usingScreenReader) {
				closeSearchField();
				e.preventDefault();
				searchToggle.focus();
			}
		});

		const closeSearchField = () => {
			body.classList.remove('search-open');
			body.classList.remove('search-is-visible');

			searchPane.style.display = 'none';
			searchPane.setAttribute('aria-hidden', true);

			searchToggle.setAttribute('aria-expanded', false);
			searchToggle.classList.add('icon-search');
			searchToggle.classList.remove('icon-x');
		};
	}

	_initStopAutoPlayOnVideo() {
		var playButton = document.getElementById('play_button');
		let video = document.getElementById('video');

		if (video) {
			playButton.addEventListener('click', function() {
				let icon = playButton.getElementsByTagName('span')[0];

				if (video.paused == true) {
					video.play();

					icon.classList.remove('icon-play');
					icon.classList.add('icon-pause');
					playButton.setAttribute('aria-label', 'Stoppa videon i bakgrunden');
				} else {
					video.pause();

					icon.classList.remove('icon-pause');
					icon.classList.add('icon-play');
					playButton.setAttribute('aria-label', 'Starta videon i bakgrunden');
				}
			});
		}
	}

	_initClearInputFieldButton() {
		let inputField = document.getElementsByClassName(
			'search-form__field-search'
		)[0];
		let clearBtn = document.getElementsByClassName('search-form__clear-btn')[0];

		if (inputField && clearBtn) {
			inputField.addEventListener('input', () => {
				if (inputField.value) {
					clearBtn.style.display = 'block';
				} else {
					clearBtn.style.display = 'none';
				}
			});

			clearBtn.addEventListener('click', e => {
				inputField.style.outline = 'none';
				clearInputField(e);
			});

			clearBtn.addEventListener('keydown', e => {
				let usingScreenReader = e.keyCode === 13 || e.keyCode === 32;

				if (usingScreenReader) {
					clearInputField(e);
				}
			});

			const clearInputField = e => {
				e.preventDefault();
				inputField.value = '';
				clearBtn.style.display = 'none';
				inputField.focus();
			};
		}
	}
}
